export function Skeleton({ className = "", ...props }) {
  return (
    <div
      className={`skeleton-shimmer rounded-lg ${className}`}
      aria-hidden
      {...props}
    />
  );
}

function HeaderSkeleton({ dark = false }) {
  return (
    <div className="max-w-3xl mx-auto text-center mb-10 sm:mb-12 space-y-4">
      <Skeleton
        className={`h-7 w-32 mx-auto rounded-full ${dark ? "skeleton-shimmer-dark" : ""}`}
      />
      <Skeleton
        className={`h-10 sm:h-12 w-full max-w-lg mx-auto ${dark ? "skeleton-shimmer-dark" : ""}`}
      />
      <Skeleton
        className={`h-5 w-full max-w-md mx-auto ${dark ? "skeleton-shimmer-dark" : ""}`}
      />
    </div>
  );
}

export function HeroSkeleton() {
  return (
    <section className="relative min-h-[100dvh] min-h-screen saas-hero-mesh pt-20 sm:pt-24 pb-12 overflow-hidden">
      <div className="page-container relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center w-full">
          <div className="lg:col-span-7 space-y-6">
            <Skeleton className="h-7 w-40 rounded-full" />
            <Skeleton className="h-14 sm:h-16 w-full" />
            <Skeleton className="h-14 sm:h-16 w-4/5" />
            <Skeleton className="h-6 w-full max-w-xl" />
            <Skeleton className="h-6 w-3/4 max-w-lg" />
            <div className="flex flex-col sm:flex-row gap-3 pt-2">
              <Skeleton className="h-12 w-full sm:w-44 rounded-md" />
              <Skeleton className="h-12 w-full sm:w-44 rounded-md" />
            </div>
          </div>
          <div className="lg:col-span-5 min-h-[220px] sm:min-h-[280px] lg:min-h-[480px]">
            <Skeleton className="w-full h-full min-h-[220px] sm:min-h-[280px] lg:min-h-[480px] rounded-2xl opacity-60" />
          </div>
        </div>
      </div>
    </section>
  );
}

export function CardsSectionSkeleton({
  dark = false,
  columns = 3,
  cards = 3,
  className = "",
}) {
  const bg = dark ? "bg-slate-900" : "bg-slate-50";
  const cardClass = dark ? "skeleton-shimmer-dark" : "";
  const gridCols =
    columns === 1
      ? ""
      : columns === 2
        ? "sm:grid-cols-2"
        : columns === 6
          ? "sm:grid-cols-2 lg:grid-cols-3"
          : "md:grid-cols-3";

  return (
    <section className={`py-12 sm:py-24 ${bg} ${className}`}>
      <div className="page-container">
        <HeaderSkeleton dark={dark} />
        <div className={`grid grid-cols-1 ${gridCols} gap-5 sm:gap-6`}>
          {Array.from({ length: cards }).map((_, i) => (
            <Skeleton
              key={i}
              className={`h-48 sm:h-56 rounded-2xl ${cardClass}`}
              style={{ animationDelay: `${i * 120}ms` }}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

export function AccordionSectionSkeleton() {
  return (
    <section className="py-12 sm:py-24 bg-white">
      <div className="page-container">
        <HeaderSkeleton />
        <div className="space-y-3 sm:space-y-4">
          {[0, 1, 2].map((i) => (
            <Skeleton
              key={i}
              className="h-16 sm:h-[72px] rounded-2xl"
              style={{ animationDelay: `${i * 100}ms` }}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

export function LogosSectionSkeleton() {
  return (
    <section className="pt-5 pb-7 sm:pt-6 sm:pb-8 bg-white border-y border-slate-100">
      <div className="page-container mb-4 sm:mb-5 flex justify-center">
        <Skeleton className="h-7 w-28 rounded-full" />
      </div>
      <div className="flex justify-center gap-3 w-full overflow-hidden px-4">
        {[0, 1, 2, 3, 4, 5].map((i) => (
          <Skeleton
            key={i}
            className="h-10 w-28 rounded-xl shrink-0"
            style={{ animationDelay: `${i * 80}ms` }}
          />
        ))}
      </div>
    </section>
  );
}

export function TestimonialsSectionSkeleton() {
  return (
    <section className="py-12 sm:py-24 bg-white">
      <div className="page-container">
        <HeaderSkeleton />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 w-full">
          {[0, 1, 2].map((i) => (
            <Skeleton
              key={i}
              className="h-52 sm:h-56 rounded-2xl"
              style={{ animationDelay: `${i * 120}ms` }}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

export function CommunitySectionSkeleton() {
  return (
    <section className="py-12 sm:py-24 bg-white">
      <div className="page-container">
        <HeaderSkeleton />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 w-full">
          {[0, 1, 2].map((i) => (
            <Skeleton
              key={i}
              className="h-72 sm:h-80 rounded-2xl"
              style={{ animationDelay: `${i * 120}ms` }}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

export function CtaSectionSkeleton() {
  return (
    <section className="py-14 sm:py-28 bg-white">
      <div className="page-container">
        <Skeleton className="h-64 sm:h-72 rounded-2xl sm:rounded-3xl mx-auto bg-slate-200" />
      </div>
    </section>
  );
}

export function DiagnosisSectionSkeleton() {
  return (
    <section className="py-12 sm:py-24 bg-white">
      <div className="page-container">
        <div className="w-full mb-10 space-y-4">
          <Skeleton className="h-7 w-36 rounded-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-6 w-full" />
        </div>
        <Skeleton className="h-28 w-full rounded-r-lg mb-12" />
        <Skeleton className="h-5 w-48 mb-6" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-40 rounded-2xl" />
          ))}
        </div>
      </div>
    </section>
  );
}

export function WhyPycraySectionSkeleton() {
  return (
    <section className="py-12 sm:py-24 bg-slate-50">
      <div className="page-container">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 w-full">
          <div className="space-y-4">
            <Skeleton className="h-7 w-32 rounded-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-24 w-full" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {[0, 1, 2, 3].map((i) => (
              <Skeleton
                key={i}
                className="h-36 rounded-2xl"
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
